// Story 16.5 — organizaciones del usuario (el staff ve todas). El admin da de
// alta un cliente acá: crea la organización y después invita a su owner desde
// la página de la organización.

import { Link, useNavigate } from '@tanstack/react-router';
import { Building2, Plus } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { toast } from 'sonner';
import { Can } from '@/components/auth/Can';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCreateOrganization, useOrganizations } from '@/hooks/queries/use-organizations';
import { ROLE_LABELS } from '@/lib/organization-roles';

function NewOrganizationForm(): React.ReactElement {
  const [name, setName] = useState('');
  const create = useCreateOrganization();
  const navigate = useNavigate();

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    try {
      const organization = await create.mutateAsync(name.trim());
      toast.success(`Organización ${organization.name} creada. Invita a su owner.`);
      setName('');
      await navigate({
        to: '/organizations/$organizationId',
        params: { organizationId: String(organization.id) },
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudo crear la organización');
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Nuevo cliente</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <div className="flex-1 space-y-1">
            <Label htmlFor="org-name">Nombre de la organización</Label>
            <Input
              id="org-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Pizzería Luigi"
              maxLength={100}
              required
            />
          </div>
          <Button type="submit" disabled={create.isPending || !name.trim()}>
            <Plus className="h-4 w-4" />
            Crear
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

export function OrganizationsPage(): React.ReactElement {
  const { data, isLoading, isError } = useOrganizations();

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Organizaciones</h1>
        <p className="text-sm text-muted-foreground">
          Cada cliente es una organización con sus bots, sus credenciales y su gente.
        </p>
      </header>

      <Can capability="manage_users">
        <NewOrganizationForm />
      </Can>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Cargando organizaciones…</p>
      ) : isError ? (
        <p className="text-sm text-destructive">No se pudieron cargar las organizaciones.</p>
      ) : !data?.length ? (
        <p className="text-sm text-muted-foreground">
          Todavía no perteneces a ninguna organización.
        </p>
      ) : (
        <ul className="divide-y divide-border rounded-md border border-border">
          {data.map((org) => (
            <li key={org.id}>
              <Link
                to="/organizations/$organizationId"
                params={{ organizationId: String(org.id) }}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 p-4 hover:bg-muted/50"
              >
                <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                <span className="min-w-0 flex-1 basis-48">
                  <span className="block font-medium wrap-break-word">{org.name}</span>
                  <span className="block truncate font-mono text-xs text-muted-foreground">
                    {org.slug}
                  </span>
                </span>
                <span className="text-sm text-muted-foreground">
                  {org.memberCount} {org.memberCount === 1 ? 'miembro' : 'miembros'} ·{' '}
                  {org.botCount} {org.botCount === 1 ? 'bot' : 'bots'}
                </span>
                {org.role && (
                  <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
                    {ROLE_LABELS[org.role]}
                  </span>
                )}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
